/*
  COMPONENT: Motion Runtime

  Owns:
  - IntersectionObserver reveal helpers
  - Shared pointer parallax for editorial media
  - Opt-in magnetic motion for CTA visual surfaces

  Does not own:
  - Section layout
  - Cart/product/search state
*/

(() => {
  function initializeReveals(root = document) {
    const elements = root.querySelectorAll('[data-motion-reveal]');
    if (!elements.length) return;

    if (!('IntersectionObserver' in window)) {
      elements.forEach((element) => element.classList.add('is-visible'));
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.16 }
    );

    elements.forEach((element, index) => {
      element.style.setProperty('--motion-reveal-delay', `${Math.min(index * 90, 360)}ms`);
      observer.observe(element);
      window.CurtisTheme?.registerCleanup?.(element, () => observer.unobserve(element));
    });
  }

  function initializeParallax(root = document) {
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

    root.querySelectorAll('[data-motion-parallax]').forEach((element) => {
      if (element.dataset.parallaxInitialized === 'true') return;

      const media = element.querySelector('[data-motion-parallax-media]');
      if (!media) return;

      element.dataset.parallaxInitialized = 'true';
      let frameId = 0;
      let nextX = 0;
      let nextY = 0;
      let isActive = false;

      const render = () => {
        if (isActive) {
          media.style.transform = `scale(1.04) translate3d(${nextX.toFixed(2)}px, ${nextY.toFixed(2)}px, 0)`;
        } else {
          media.style.removeProperty('transform');
        }
        frameId = 0;
      };

      const queueRender = () => {
        if (frameId) return;
        frameId = window.requestAnimationFrame(render);
      };

      const handlePointerMove = (event) => {
        if (event.pointerType && event.pointerType !== 'mouse') return;
        const rect = element.getBoundingClientRect();
        if (!rect.width || !rect.height) return;

        nextX = ((event.clientX - rect.left) / rect.width - 0.5) * 12;
        nextY = ((event.clientY - rect.top) / rect.height - 0.5) * 12;
        isActive = true;
        queueRender();
      };

      const reset = () => {
        isActive = false;
        queueRender();
      };

      element.addEventListener('pointermove', handlePointerMove, { passive: true });
      element.addEventListener('pointerleave', reset);
      element.addEventListener('pointercancel', reset);

      window.CurtisTheme?.registerCleanup?.(element, () => {
        element.removeEventListener('pointermove', handlePointerMove);
        element.removeEventListener('pointerleave', reset);
        element.removeEventListener('pointercancel', reset);
        if (frameId) window.cancelAnimationFrame(frameId);
        media.style.removeProperty('transform');
        delete element.dataset.parallaxInitialized;
      });
    });
  }

  function initializeMagneticButtons(root = document) {
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

    root.querySelectorAll('[data-magnetic]').forEach((element) => {
      if (element.dataset.magneticInitialized === 'true') return;

      const surface = element.querySelector('[data-magnetic-surface]');
      if (!surface) return;

      element.dataset.magneticInitialized = 'true';
      let frameId = 0;
      let nextX = 0;
      let nextY = 0;

      const render = () => {
        surface.style.setProperty('--magnetic-x', `${nextX.toFixed(2)}px`);
        surface.style.setProperty('--magnetic-y', `${nextY.toFixed(2)}px`);
        frameId = 0;
      };

      const queueRender = () => {
        if (frameId) return;
        frameId = window.requestAnimationFrame(render);
      };

      const reset = () => {
        nextX = 0;
        nextY = 0;
        element.classList.remove('is-magnetic-active');
        queueRender();
      };

      const handlePointerEnter = (event) => {
        if (event.pointerType && event.pointerType !== 'mouse') return;
        element.classList.add('is-magnetic-active');
      };

      const handlePointerMove = (event) => {
        if (event.pointerType && event.pointerType !== 'mouse') return;

        const rect = element.getBoundingClientRect();
        if (!rect.width || !rect.height) return;

        const normalizedX = Math.max(-1, Math.min(1, ((event.clientX - rect.left) / rect.width - 0.5) * 2));
        const normalizedY = Math.max(-1, Math.min(1, ((event.clientY - rect.top) / rect.height - 0.5) * 2));
        const maxDisplacement = 6;

        nextX = normalizedX * maxDisplacement;
        nextY = normalizedY * maxDisplacement;
        queueRender();
      };

      element.addEventListener('pointerenter', handlePointerEnter);
      element.addEventListener('pointermove', handlePointerMove);
      element.addEventListener('pointerleave', reset);
      element.addEventListener('pointercancel', reset);
      element.addEventListener('blur', reset);

      window.CurtisTheme?.registerCleanup?.(element, () => {
        element.removeEventListener('pointerenter', handlePointerEnter);
        element.removeEventListener('pointermove', handlePointerMove);
        element.removeEventListener('pointerleave', reset);
        element.removeEventListener('pointercancel', reset);
        element.removeEventListener('blur', reset);
        if (frameId) window.cancelAnimationFrame(frameId);
      });
    });
  }

  function initialize(root = document) {
    initializeReveals(root);
    initializeParallax(root);
    initializeMagneticButtons(root);
  }

  window.CurtisMotion = {
    initialize,
  };

  initialize();
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();
